import { createTheme } from '@mui/material/styles';
import { OrderStatus } from '../types/models';

// Shetkari logo inspired color palette - Natural farm-to-table theme
const colors = {
  primary: {
    main: '#7CB342', // Vibrant leaf green from logo
    dark: '#558B2F',
    light: '#AED581',
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#FF8F00', // Warm orange from farmer's turban
    dark: '#E65100',
    light: '#FFB74D',
    contrastText: '#FFFFFF'
  },
  accent: {
    main: '#8BC34A', // Fresh green accent
    dark: '#689F38',
    light: '#C5E1A5',
    contrastText: '#FFFFFF'
  },
  status: {
    success: '#7CB342', // Use primary green for success
    warning: '#FF8F00', // Use secondary orange for warning
    error: '#D32F2F',
    info: '#1976D2'
  },
  orderStatus: {
    pending: '#FF8F00', // Warm orange
    confirmed: '#1976D2', // Blue
    deliveryAssigned: '#7B1FA2', // Purple
    outForDelivery: '#0097A7', // Teal
    delivered: '#7CB342', // Primary green
    cancelled: '#D32F2F', // Red
    returned: '#F57C00', // Deep Orange
    refunded: '#5D4037' // Brown
  },
  neutral: {
    white: '#FFFFFF',
    black: '#000000',
    grey: '#9E9E9E',
    greyLight: '#E0E0E0',
    greyDark: '#616161'
  },
  background: {
    default: '#F8F9FA', // Slightly warmer background
    paper: '#FFFFFF',
    card: '#FFFFFF'
  },
  text: {
    primary: '#2E3440', // Warmer dark text
    secondary: '#5E6B73',
    light: '#9CA3AF',
    onPrimary: '#FFFFFF'
  },
  border: {
    main: '#E2E8F0',
    light: '#F7FAFC'
  }
};

// Create MUI theme
export const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    error: {
      main: colors.status.error
    },
    warning: {
      main: colors.status.warning
    },
    info: {
      main: colors.status.info
    },
    success: {
      main: colors.status.success
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary
    },
    grey: {
      50: '#F9FAFB',
      100: '#F7FAFC',
      200: '#EDF2F7',
      300: '#E2E8F0',
      400: '#CBD5E0',
      500: '#A0AEC0',
      600: '#718096',
      700: '#4A5568',
      800: '#2D3748',
      900: '#1A202C'
    }
  },
  typography: {
    fontFamily: '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: colors.text.primary
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.text.primary
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.text.primary
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      color: colors.text.primary
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: colors.text.secondary
    },
    button: {
      textTransform: 'none',
      fontWeight: 600
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: colors.border.main
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: colors.background.default,
            fontWeight: 600,
            color: colors.text.primary
          }
        }
      }
    }
  }
});

// Helper function to get order status color
export const getOrderStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.PENDING:
      return colors.orderStatus.pending;
    case OrderStatus.CONFIRMED:
      return colors.orderStatus.confirmed;
    case OrderStatus.DELIVERY_ASSIGNED:
      return colors.orderStatus.deliveryAssigned;
    case OrderStatus.OUT_FOR_DELIVERY:
      return colors.orderStatus.outForDelivery;
    case OrderStatus.DELIVERED:
      return colors.orderStatus.delivered;
    case OrderStatus.CANCELLED:
      return colors.orderStatus.cancelled;
    case OrderStatus.RETURNED:
      return colors.orderStatus.returned;
    case OrderStatus.REFUNDED:
      return colors.orderStatus.refunded;
    default:
      return colors.neutral.grey;
  }
};

// Helper function to get light version of order status color
export const getOrderStatusLightColor = (status: OrderStatus): string => {
  const color = getOrderStatusColor(status);
  return `${color}20`; // Add 20% opacity
};

export default theme;
